import AppPageTitle from '@/components/AppPageTitle';
import AppTable from '@/components/AppTable';
import AppTableQuery from '@/components/AppTableQuery';
import PATH from '@/config/paths/PATH';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTopics } from '@/services/topic.service';
import useTopic from '@/redux/hooks/useTopic';
import { exportToExcel } from '@/utils';
import { formatDate, columnSorter } from '@/utils/formatters';
import { Button, Space } from 'antd';
import { FileExcelOutlined } from '@ant-design/icons';
import usePaginatedQuery from '@/hooks/usePaginatedQuery';
import excelExample from '../../assets/excel/Magic_Wheel_Cleaned.xlsx';
import axiosClient from '@/api/axiosClient';
import { API_GET_TOPICS } from '@/config/paths/API_PATH';
import dayjs from 'dayjs';
import { getActiveBadge } from '@/utils/getActiveBadge';

const filterOptions = [
  {
    value: null,
    label: 'All',
  },
  {
    value: '1',
    label: 'Active',
  },
  {
    value: '0',
    label: 'Inactive',
  },
];

const columns = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    sorter: columnSorter('id', 'number'),
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Name (English)',
    dataIndex: 'name_en',
    key: 'name_en',
    sorter: columnSorter('name_en'),
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Name (Myanmar)',
    dataIndex: 'name_my',
    key: 'name_my',
    render: (value) => (value ? value : '-'),
  },
  {
    title: 'Status',
    dataIndex: 'is_active',
    key: 'is_active',
    render: (is_active) => {
      return getActiveBadge(+is_active === 1, {
        activeText: 'Active',
        inactiveText: 'Inactive',
      });
    },
    sorter: columnSorter('is_active', 'number'),
  },
  {
    title: 'Created At',
    dataIndex: 'created_at',
    key: 'created_at',
    render: (date) => (date ? formatDate(date) : '-'),
    sorter: columnSorter('created_at', 'date'),
  },
];

function Topic() {
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [queryValues, setQueryValues] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const offset = (pageIndex - 1) * pageSize + 1;
  const { setEditingTopicValues } = useTopic();
  // const [isExport, setIsExport] = useState(false);

  const {
    data: topicsData,
    isLoading,
    refetch,
    isRefetching,
  } = usePaginatedQuery(
    () =>
      getTopics({
        pageSize,
        offset,
        name: queryValues?.searchValue,
        isActive: queryValues?.selectedFilter,
      }),
    ['topics', pageSize, offset, queryValues]
  );

  const handleCreateTopic = () => navigate(PATH.TOPIC.CREATE);

  const handleQuery = () => {
    setQueryValues({
      searchValue,
      selectedFilter,
    });
    setPageIndex(1);
  };

  const handleReset = () => {
    setSearchValue(null);
    setSelectedFilter(null);
    setQueryValues(null);
    setPageIndex(1);
  };

  const handleEditTopic = (record) => {
    setEditingTopicValues(record);
    navigate(PATH.TOPIC.UPDATE.replace(':id', record.id));
  };

  const handleOnRefresh = () => {
    refetch();
  };

  const handleExport = async () => {
    const { data } = await axiosClient.get(API_GET_TOPICS);
    const exportData = data.dataRes.sort((a, b) => a.id - b.id);
    const fileName = `topics-${dayjs().format('YYYY-MM-DD')}.xlsx`;
    const columnMapping = {
      id: 'ID',
      name_en: 'Name (English)',
      name_my: 'Name (Myanmar)',
      is_active: 'Status',
      created_at: 'Created At',
    };

    const formattedData = exportData?.map((item) => ({
      ...item,
      is_active: item.is_active === 1 ? 'Active' : 'Inactive',
      created_at: formatDate(item.created_at),
    }));

    exportToExcel(formattedData, {
      fileName,
      columnMapping,
      styled: true,
    });
  };

  // const handleToTopicConfig = () => navigate(PATH.TOPIC.CONFIG);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = excelExample;
    link.download = 'Magic_Wheel_Cleaned.xlsx';
    link.click();
  };

  return (
    <Space direction="vertical" className="w-full">
      <Space className="flex justify-between items-center">
        <AppPageTitle title="Topics" />

        {/* <Tooltip title="To topic configuration">
          <Button onClick={handleToTopicConfig} type="text">
            <SettingOutlined />
          </Button>
        </Tooltip> */}
        <Button type="primary" icon={<FileExcelOutlined />} onClick={handleDownload}>
          Example Excel file
        </Button>
      </Space>
      <AppTableQuery
        filterOptions={filterOptions}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        filterPlaceholder="Status"
        searchPlaceholder="Search topic name"
        filterLabel="Status"
        handleReset={handleReset}
        handleQuery={handleQuery}
        loading={isLoading || isRefetching}
      />
      <AppTable
        columns={columns}
        hasRowAction
        dataSource={topicsData?.dataRes}
        totalItems={topicsData?.total}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={(page, size) => {
          setPageIndex(page);
        }}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
        }}
        loading={isLoading || isRefetching}
        onEditAction={handleEditTopic}
        onCreate={handleCreateTopic}
        onRefresh={handleOnRefresh}
        onExport={handleExport}
        hasImport
      />
    </Space>
  );
}

export default Topic;
